import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputBase,
  IconButton,
  TextField,
  Select,
  MenuItem,
  TablePagination,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  GetApp as GetAppIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { translateText } from "../translations/translationService";
import { usePagination } from "../hooks";
import * as XLSX from "xlsx";
import { getAssignmentPriceLines } from "../services/api/reports/AssignmentPriceLinesApi";
import { notify } from "../services/ui/notify";
import { CONTROL_REPARACIONES_SCREEN_COLORS, TEXT_COLORS } from "../style/colors.js";

const formatDateOnly = (fecha) => {
  if (!fecha) return "";
  if (typeof fecha === "string") {
    const [datePart] = fecha.split(" ");
    return datePart || fecha;
  }
  if (typeof fecha === "object" && fecha !== null && fecha.date) {
    const dateStr = typeof fecha.date === "string" ? fecha.date : String(fecha.date);
    const [datePart] = dateStr.split(" ");
    return datePart ? datePart.substring(0, 10) : "";
  }
  return "";
};

/** Formato dd-mm-aaaa para mostrar en tabla */
const formatDateDDMMYYYY = (fecha) => {
  const raw = formatDateOnly(fecha);
  if (!raw || raw.length < 10) return raw;
  const [y, m, d] = raw.substring(0, 10).split("-");
  if (d && m && y) return `${d}-${m}-${y}`;
  return raw;
};

const formatUsuario = (user) =>
  (user || "").replace(/^EXTERNO_/i, "").trim() || (user || "");

const formatNumber = (value, decimals = 2) => {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return n.toFixed(decimals);
};

// Normaliza "WO000187" => 187 para ordenar por número de WO (desc).
const parseWoNumber = (woNum) => {
  if (!woNum) return 0;
  const digits = String(woNum).replace(/\D/g, "");
  const n = Number(digits);
  return Number.isNaN(n) ? 0 : n;
};

export default function AssignmentPriceLinesReportScreen() {
  const lang = useSelector((state) => state.lang.value);
  const usuario = useSelector((state) => state.user.name);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterUsuario, setFilterUsuario] = useState("");
  const [expandedWo, setExpandedWo] = useState(new Set());
  const [expandedMo, setExpandedMo] = useState(new Set());

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination(0, 100);

  const toggleWo = (workOrderNumber) => {
    setExpandedWo((prev) => {
      const next = new Set(prev);
      if (next.has(workOrderNumber)) next.delete(workOrderNumber);
      else next.add(workOrderNumber);
      return next;
    });
  };

  const moKey = (woNum, mo) => `${woNum ?? ""}-${mo ?? ""}`;
  const toggleMo = (workOrderNumber, mo) => {
    const key = moKey(workOrderNumber, mo);
    setExpandedMo((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const data = await getAssignmentPriceLines();
      setRows(Array.isArray(data) ? data : []);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const uniqueUsuarios = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((wo) => {
      (wo.piezas || []).forEach((p) => {
        const u = p.Usuario ?? p.usuario;
        if (u) set.add(u);
      });
    });
    return Array.from(set).sort();
  }, [rows]);

  const piezaMatchesFilters = (pieza) => {
    const user = pieza.Usuario ?? pieza.usuario;
    if (filterUsuario && user !== filterUsuario) return false;
    const rowDate = formatDateOnly(pieza.fecha);
    if (filterDateFrom && rowDate < filterDateFrom) return false;
    if (filterDateTo && rowDate > filterDateTo) return false;
    const term = searchText.trim().toLowerCase();
    if (!term) return true;
    const fields = [
      pieza.po_number,
      pieza.mo,
      pieza.work_order_number,
      pieza.tipo,
      pieza.referencia,
      pieza.obs,
    ];
    if (fields.some((f) => f && String(f).toLowerCase().includes(term)))
      return true;
    const numericFields = [
      pieza.cantidad_mo,
      pieza.numero_piedras_total,
      pieza.precio_total,
    ];
    if (
      numericFields.some(
        (f) => f != null && String(f).toLowerCase().includes(term)
      )
    )
      return true;
    const dateStr = formatDateOnly(pieza.fecha);
    if (dateStr && dateStr.toLowerCase().includes(term)) return true;
    const lineas = pieza.lineas;
    if (Array.isArray(lineas)) {
      if (
        lineas.some(
          (l) =>
            l.ref_piedra && String(l.ref_piedra).toLowerCase().includes(term)
        )
      )
        return true;
    }
    return false;
  };

  const filteredRows = useMemo(() => {
    return (rows || [])
      .filter((wo) =>
        (wo.piezas || []).some((p) => piezaMatchesFilters(p))
      )
      .sort(
        (a, b) =>
          parseWoNumber(b.work_order_number) -
          parseWoNumber(a.work_order_number)
      );
  }, [rows, searchText, filterDateFrom, filterDateTo, filterUsuario]);

  const paginatedWos = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleClearSearch = () => {
    setSearchText("");
  };

  const handleClearFilters = () => {
    setSearchText("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterUsuario("");
  };

  const hasActiveFilters =
    searchText.trim() ||
    filterDateFrom ||
    filterDateTo ||
    filterUsuario;

  const handleExportToExcel = () => {
    if (!filteredRows.length) {
      notify.info(translateText("No hay datos para exportar.", lang));
      return;
    }

    const dataToExport = [];
    const lineasRows = [];
    filteredRows.forEach((wo) => {
      (wo.piezas || []).forEach((pieza) => {
        dataToExport.push({
          "Work Order": wo.work_order_number || "",
          PO: pieza.po_number || "",
          MO: pieza.mo || "",
          Tipo: pieza.tipo || "",
          Fecha: formatDateDDMMYYYY(pieza.fecha),
          Referencia: pieza.referencia || "",
          Observaciones: pieza.obs || "",
          "Cantidad MO": pieza.cantidad_mo != null ? Number(pieza.cantidad_mo) : "",
          "Número piedras total": pieza.numero_piedras_total != null ? Number(pieza.numero_piedras_total) : "",
          "Precio total": pieza.precio_total != null && pieza.precio_total !== "" ? Number(pieza.precio_total) : "",
          Usuario: formatUsuario(pieza.Usuario ?? pieza.usuario ?? ""),
        });
        (pieza.lineas || []).forEach((l) => {
          lineasRows.push({
            "Work Order": wo.work_order_number || "",
            MO: pieza.mo || "",
            "Ref. piedra": l.ref_piedra || "",
            "Nº piedras": l.numero_piedras != null ? Number(l.numero_piedras) : "",
            "Precio total": l.precio_total != null && l.precio_total !== "" ? Number(l.precio_total) : "",
          });
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Fecha: convertir dd-mm-aaaa a Excel date y formato fecha; Precio total: formato numérico con 2 decimales (0.00)
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let R = 1; R <= range.e.r; R++) {
      const fechaCell = XLSX.utils.encode_cell({ r: R, c: 4 });
      const precioCell = XLSX.utils.encode_cell({ r: R, c: 9 });
      const cantMoCell = XLSX.utils.encode_cell({ r: R, c: 7 });
      const numPiedrasCell = XLSX.utils.encode_cell({ r: R, c: 8 });
      const f = worksheet[fechaCell];
      if (f && f.v) {
        const str = String(f.v);
        const m = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (m) {
          const [, d, mon, y] = m;
          const date = new Date(Number(y), Number(mon) - 1, Number(d));
          const serial = 1 + (date.getTime() - new Date(1900, 0, 1).getTime()) / 86400000;
          worksheet[fechaCell] = { t: "n", v: serial, z: "dd-mm-yyyy" };
        }
      }
      const p = worksheet[precioCell];
      if (p && (p.t === "n" || typeof p.v === "number")) {
        worksheet[precioCell] = { t: "n", v: Number(p.v), z: "0.00" };
      }
      const c = worksheet[cantMoCell];
      if (c != null && c.v !== "" && !Number.isNaN(Number(c.v))) {
        worksheet[cantMoCell] = { t: "n", v: Number(c.v) };
      }
      const n = worksheet[numPiedrasCell];
      if (n != null && n.v !== "" && !Number.isNaN(Number(n.v))) {
        worksheet[numPiedrasCell] = { t: "n", v: Number(n.v) };
      }
    }

    worksheet["!autofilter"] = { ref: worksheet["!ref"] };

    const wch = (arr, min = 8) => (arr.length ? Math.max(min, ...arr.map((s) => String(s).length)) : min);
    const colWidths = [
      { wch: Math.max(11, dataToExport.length ? wch(dataToExport.map((r) => r["Work Order"] || ""), 11) : 11) },
      { wch: Math.max(8, dataToExport.length ? wch(dataToExport.map((r) => r.PO || ""), 8) : 8) },
      { wch: Math.max(12, dataToExport.length ? wch(dataToExport.map((r) => r.MO || ""), 12) : 12) },
      { wch: Math.max(18, dataToExport.length ? wch(dataToExport.map((r) => r.Tipo || ""), 18) : 18) },
      { wch: 12 },
      { wch: Math.max(15, dataToExport.length ? wch(dataToExport.map((r) => r.Referencia || ""), 15) : 15) },
      { wch: Math.max(25, dataToExport.length ? wch(dataToExport.map((r) => r.Observaciones || ""), 25) : 25) },
      { wch: 12 },
      { wch: Math.max(10, dataToExport.length ? wch(dataToExport.map((r) => r["Número piedras total"] ?? ""), 10) : 10) },
      { wch: 12 },
      { wch: Math.max(25, dataToExport.length ? wch(dataToExport.map((r) => r.Usuario || ""), 25) : 25) },
    ];
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "assignment_price_lines"
    );
    if (lineasRows.length) {
      const sheetLineas = XLSX.utils.json_to_sheet(lineasRows);
      const rangeL = XLSX.utils.decode_range(sheetLineas["!ref"]);
      for (let R = 1; R <= rangeL.e.r; R++) {
        const precioCell = XLSX.utils.encode_cell({ r: R, c: 4 });
        const p = sheetLineas[precioCell];
        if (p && (p.t === "n" || typeof p.v === "number")) {
          sheetLineas[precioCell] = { t: "n", v: Number(p.v), z: "0.00" };
        }
      }
      sheetLineas["!autofilter"] = { ref: sheetLineas["!ref"] };
      sheetLineas["!cols"] = [
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 10 },
        { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(workbook, sheetLineas, "lineas_piedras");
    }

    XLSX.writeFile(
      workbook,
      `assignment_price_lines_${usuario || "user"}.xlsx`
    );
  };

  return (
    <Box sx={{ padding: 2 }}>
      {/* Filtros dentro de módulo blanco (estilo Trabajo Externo) */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${CONTROL_REPARACIONES_SCREEN_COLORS?.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}
      >
        {/* Buscador */}
        <Paper
          variant="outlined"
          sx={{
            display: "flex",
            alignItems: "center",
            width: 320,
            minHeight: 36,
            px: 1,
            backgroundColor: CONTROL_REPARACIONES_SCREEN_COLORS?.BACKGROUND_CARD || "#F9FAFB",
            border: `1.5px solid ${CONTROL_REPARACIONES_SCREEN_COLORS?.BORDER_CARD || "#e5e7eb"}`,
            borderRadius: 2,
          }}
        >
          <SearchIcon sx={{ mr: 1, color: TEXT_COLORS.SECONDARY, fontSize: 20 }} />
          <InputBase
            placeholder={translateText(
              "Buscar por cualquier campo...",
              lang
            )}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flex: 1, fontSize: "0.85rem" }}
          />
          {searchText ? (
            <IconButton size="small" onClick={handleClearSearch}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Paper>

        {/* Rango de fechas: desde */}
        <TextField
          label={translateText("Fecha desde", lang)}
          type="date"
          size="small"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: 160,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              height: 36,
              fontSize: "0.8rem",
            },
          }}
        />

        {/* Rango de fechas: hasta */}
        <TextField
          label={translateText("Fecha hasta", lang)}
          type="date"
          size="small"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: 160,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              height: 36,
              fontSize: "0.8rem",
            },
          }}
        />

        {/* Filtro por usuario */}
        <Select
          size="small"
          value={filterUsuario}
          onChange={(e) => setFilterUsuario(e.target.value)}
          displayEmpty
          sx={{
            minWidth: 200,
            height: 36,
            backgroundColor: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: CONTROL_REPARACIONES_SCREEN_COLORS?.BORDER_CARD || "#e5e7eb",
            },
          }}
        >
          <MenuItem value="">
            {translateText("Todos los usuarios", lang)}
          </MenuItem>
          {uniqueUsuarios.map((u) => (
            <MenuItem key={u} value={u}>
              {u}
            </MenuItem>
          ))}
        </Select>

        {/* Quitar filtros */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
          sx={{
            height: 36,
            fontSize: "0.8rem",
            textTransform: "none",
            borderColor: CONTROL_REPARACIONES_SCREEN_COLORS?.BORDER_BRAND || "#174045",
            color: CONTROL_REPARACIONES_SCREEN_COLORS?.TEXT_BRAND || "#174045",
            "&:hover": {
              borderColor: CONTROL_REPARACIONES_SCREEN_COLORS?.BORDER_BRAND || "#174045",
              backgroundColor: "rgba(23, 64, 69, 0.08)",
            },
            "&.Mui-disabled": {
              borderColor: "action.disabled",
              color: "action.disabled",
            },
          }}
        >
          {translateText("Quitar filtros", lang)}
        </Button>

        <Box sx={{ flex: 1 }} />

        {/* Exportar excel */}
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleExportToExcel}
          size="small"
          sx={{
            height: 36,
            fontSize: "0.8rem",
            textTransform: "none",
            borderColor: CONTROL_REPARACIONES_SCREEN_COLORS?.BORDER_BRAND || "#174045",
            color: CONTROL_REPARACIONES_SCREEN_COLORS?.TEXT_BRAND || "#174045",
            "&:hover": {
              borderColor: CONTROL_REPARACIONES_SCREEN_COLORS?.BORDER_BRAND || "#174045",
              backgroundColor: "rgba(23, 64, 69, 0.08)",
            },
          }}
        >
          {translateText("Exportar excel", lang)}
        </Button>
      </Box>

      {/* Tabla: agrupada por WO; desplegar WO → piezas (MO); desplegar MO → lineas */}
      <Paper>
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader size="small" sx={{ tableLayout: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 36, py: 0.5, px: 0.5 }} />
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>PO</TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>MO</TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>WO</TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>Tipo</TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Fecha", lang)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Referencia", lang)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Observaciones", lang)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Cantidad MO", lang)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Número piedras total", lang)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Precio total", lang)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Ref. piedra", lang)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem" }}>
                  {translateText("Usuario", lang)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="body2">
                      {translateText("Cargando datos...", lang)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedWos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="body2">
                      {translateText(
                        "No hay datos para mostrar.",
                        lang
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedWos.map((wo) => {
                  const woNum = wo.work_order_number ?? "";
                  const piezas = Array.isArray(wo.piezas) ? wo.piezas : [];
                  const woExpanded = expandedWo.has(woNum);

                  return (
                    <React.Fragment key={woNum}>
                      {/* Fila orden de trabajo (WO) */}
                      <TableRow
                        hover
                        onClick={() => toggleWo(woNum)}
                        sx={{
                          cursor: "pointer",
                          bgcolor: "grey.100",
                          "&:hover": { bgcolor: "grey.200" },
                        }}
                      >
                        <TableCell padding="checkbox">
                          {piezas.length > 0 ? (
                            woExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )
                          ) : null}
                        </TableCell>
                        <TableCell colSpan={2} />
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          {woNum}
                        </TableCell>
                        <TableCell align="center" colSpan={6}>
                          {piezas.length} {translateText("pieza(s)", lang)}
                        </TableCell>
                        <TableCell colSpan={3} />
                      </TableRow>
                      {/* Filas piezas (MO) al desplegar WO */}
                      {woExpanded &&
                        piezas.map((pieza) => {
                          const mo = pieza.mo ?? "";
                          const lineas = Array.isArray(pieza.lineas)
                            ? pieza.lineas
                            : [];
                          const moExpanded = expandedMo.has(moKey(woNum, mo));

                          const compactSx = {
                            py: 0.25,
                            px: 0.5,
                            fontSize: "0.75rem",
                          };

                          return (
                            <React.Fragment key={moKey(woNum, mo)}>
                              <TableRow
                                hover
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMo(woNum, mo);
                                }}
                                sx={{ cursor: "pointer" }}
                              >
                                <TableCell padding="checkbox" sx={compactSx}>
                                  {lineas.length > 0 ? (
                                    moExpanded ? (
                                      <ExpandLessIcon fontSize="small" />
                                    ) : (
                                      <ExpandMoreIcon fontSize="small" />
                                    )
                                  ) : null}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {pieza.po_number || ""}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>{pieza.mo || ""}</TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {pieza.work_order_number || ""}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {pieza.tipo || ""}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {formatDateDDMMYYYY(pieza.fecha)}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {pieza.referencia || ""}
                                </TableCell>
                                <TableCell align="center" sx={compactSx} title={pieza.obs || ""}>
                                  {(pieza.obs || "").slice(0, 20)}
                                  {(pieza.obs || "").length > 20 ? "…" : ""}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {pieza.cantidad_mo ?? ""}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {pieza.numero_piedras_total ?? ""}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {formatNumber(pieza.precio_total, 2)}
                                </TableCell>
                                <TableCell align="center" sx={compactSx}>
                                  {lineas.length ? `(${lineas.length})` : "—"}
                                </TableCell>
                                <TableCell align="center" sx={compactSx} title={formatUsuario(pieza.Usuario ?? pieza.usuario)}>
                                  {formatUsuario(pieza.Usuario ?? pieza.usuario ?? "").slice(0, 25)}
                                  {(formatUsuario(pieza.Usuario ?? pieza.usuario ?? "") || "").length > 25 ? "…" : ""}
                                </TableCell>
                              </TableRow>
                              {/* Filas lineas de piedras al desplegar MO */}
                              {moExpanded &&
                                lineas.map((lin, i) => (
                                  <TableRow
                                    key={`${moKey(woNum, mo)}-lin-${i}`}
                                    sx={{ bgcolor: "action.hover" }}
                                  >
                                    <TableCell padding="checkbox" sx={compactSx} />
                                    <TableCell colSpan={7} sx={{ ...compactSx, pl: 2 }}>—</TableCell>
                                    <TableCell align="center" sx={compactSx} />
                                    <TableCell align="center" sx={compactSx}>
                                      {lin.numero_piedras ?? ""}
                                    </TableCell>
                                    <TableCell align="center" sx={compactSx}>
                                      {formatNumber(lin.precio_total, 2)}
                                    </TableCell>
                                    <TableCell align="center" sx={compactSx}>
                                      {lin.ref_piedra || ""}
                                    </TableCell>
                                    <TableCell align="center" sx={compactSx} />
                                  </TableRow>
                                ))}
                            </React.Fragment>
                          );
                        })}
                      {/* Fila total: "Total orden" en columna Número piedras total, importe en Precio total */}
                      {woExpanded && piezas.length > 0 && (() => {
                        const totalPrecioWo = piezas.reduce(
                          (acc, p) => acc + (Number(p.precio_total) || 0),
                          0
                        );
                        const compactSx = { py: 0.25, px: 0.5, fontSize: "0.75rem" };
                        return (
                          <TableRow
                            sx={{
                              bgcolor: "grey.200",
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <TableCell padding="checkbox" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={{ ...compactSx, fontWeight: 600 }}>
                              {translateText("Total orden", lang)}
                            </TableCell>
                            <TableCell align="center" sx={{ ...compactSx, fontWeight: 600, minWidth: 64 }}>
                              {formatNumber(totalPrecioWo, 2)}
                            </TableCell>
                            <TableCell align="center" sx={compactSx} />
                            <TableCell align="center" sx={compactSx} />
                          </TableRow>
                        );
                      })()}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>
    </Box>
  );
}

